import React from 'react';
import { Flex, TextV2, ButtonV2, useDisclosure } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { ResponsiveDialog } from '@apps/card/components/ResponsiveDialog';
import { useClearContactListController } from './ClearContactList.controller';

interface ClearContactListProps
  extends Readonly<{
    contactCollectionId: string;
  }> {}

export const ClearContactList: React.FC<ClearContactListProps> = ({ contactCollectionId }) => {
  const dialogDisclosure = useDisclosure();
  const { tq } = useTranslation('contactCollector');
  const { isSuperAdmin } = useAuth();
  const { clearContactList, isClearContactListLoading, hasClearContactListError } = useClearContactListController(contactCollectionId, dialogDisclosure.onClose);

  const handleOnClearContactListClick = () => {
    clearContactList();
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <>
      <ResponsiveDialog
        isOpen={dialogDisclosure.isOpen}
        onClose={dialogDisclosure.onClose}
        header={() => <TextV2 typographyVariant="hed3">{tq`Clear Contact List`}</TextV2>}
        content={() => (
          <Flex
            flexDirection="column"
            gap={8}
          >
            <TextV2
              fontSize="15px"
              lineHeight="21px"
            >
              {tq`I understand that by clicking Clear Contact List, I will no longer be able to recover the contacts.`}
            </TextV2>
            <Flex
              gap={4}
              flexDirection={{ _: 'column-reverse', sm2: 'row' }}
              width="100%"
              justifyContent="space-between"
            >
              <ButtonV2
                intent="neutral"
                shape="rounded"
                variant="outline"
                onClick={dialogDisclosure.onClose}
              >
                {tq`Cancel`}
              </ButtonV2>
              <ButtonV2
                intent="neutral"
                shape="rounded"
                width="185px"
                onClick={handleOnClearContactListClick}
                loading={isClearContactListLoading}
              >
                {tq`Clear Contact List`}
              </ButtonV2>
            </Flex>
            {hasClearContactListError && (
              <TextV2
                typographyVariant="label2"
                color="negative5"
              >
                {tq`There was an error clearing the Contact List`}
              </TextV2>
            )}
          </Flex>
        )}
      />
      <Flex
        width="375px"
        marginBottom="5px"
      >
        <TextV2
          typographyVariant="label1"
          color="#4951EF"
          marginTop="24px"
        >
          {tq`SUPER ADMIN ONLY`}
        </TextV2>
      </Flex>
      <ButtonV2
        intent="newPrimary"
        shape="rounded"
        width="375px"
        onClick={dialogDisclosure.onOpen}
        disabled={!contactCollectionId}
      >
        {tq`Clear Contact List`}
      </ButtonV2>
    </>
  );
};
